import React from 'react'
import clsx from 'clsx'
import NextImage from 'next/image'
import NextLink from 'next/link'
import xss from 'xss'

import { Text } from '@wartek-id/text-v1'
import { Icon } from '@wartek-id/icon'

import { setURLQuery } from 'utils/url'
import { TAGS_ICON } from 'app/SearchPage/constants'

import type {
  TContentSearchResult,
  TContentSearchResultTag,
} from 'types/content-search'

const DEFAULT_THUMBNAIL = `${process.env.NEXT_PUBLIC_STATIC_GCS_CDN}/images/default-thumbnail.png`
const PATH_WITH_FROM_QUERY = ['/perangkat-ajar', '/video-inspirasi']

interface ISearchResultTagProps {
  data: TContentSearchResultTag
}

export const SearchResultTag = ({ data }: ISearchResultTagProps) => {
  const { label, icon } = data
  const tagIcon = TAGS_ICON?.[icon] ?? 'info_outline'
  const tagIconPath = `/icon/${tagIcon}.svg`

  return (
    <div
      data-testid="search-result-card--tag"
      className="flex mb-2 space-x-2 flex-nowrap"
    >
      <div className="flex items-center flex-none w-4 h-4 grow-0">
        <NextImage
          className="object-cover object-center rounded-md"
          src={tagIconPath}
          height="16px"
          width="16px"
        />
      </div>
      <Text variant="helper-bold" className="overflow-wrap-anywhere">
        {label}
      </Text>
    </div>
  )
}

interface ISearchResultImageProps {
  src: string
  isRounded?: boolean
}

export const SearchResultImage = ({
  src,
  isRounded,
}: ISearchResultImageProps) => {
  const [imgSrc, setImgSrc] = React.useState(src || DEFAULT_THUMBNAIL)
  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/alt-text */}
      <img
        data-testid="search-result-card--thumbnail"
        className={clsx(
          'object-cover object-center aspect-square',
          isRounded && 'rounded-xl'
        )}
        src={imgSrc}
        onError={() => {
          setImgSrc(DEFAULT_THUMBNAIL)
        }}
      />
    </>
  )
}

interface ISearchResultCardProps extends React.HTMLProps<HTMLDivElement> {
  item: TContentSearchResult
  isThumbnailRounded: boolean
  link?: string // Override link to be passed to NextLink,
}

const SearchResultCard = ({
  item,
  isThumbnailRounded,
  link = undefined,
  ...props
}: ISearchResultCardProps) => {
  const hasTags = React.useMemo(
    () => (item?.tags ?? []).length > 0,
    [item?.tags]
  )
  // take top 3 tags item that not null
  const displayedTags = React.useMemo(
    () =>
      (item?.tags ?? [])
        .filter(({ icon = '', label = '' }) => Boolean(icon) && Boolean(label))
        .slice(0, 3) as TContentSearchResultTag[],
    [item?.tags]
  )
  const isThumbnailVideo = React.useMemo(
    () => item?.thumbnail?.isVideo,
    [item?.thumbnail?.isVideo]
  )
  const fallbackUrl = React.useMemo(() => {
    let url = item?.url ?? '#'
    if (PATH_WITH_FROM_QUERY.find((path) => url.includes(path))) {
      url = setURLQuery(url, {
        from: '/pencarian',
      })
    }
    return url
  }, [item?.url])

  return (
    <div
      {...props}
      data-testid="search-result-card"
      className="px-4 py-6 border-b last-of-type:border-b-0"
    >
      <NextLink href={link ?? fallbackUrl} passHref>
        <a>
          <div className="flex space-x-4 space-b-2">
            <div className="relative flex-none w-16 h-16">
              <SearchResultImage
                src={item?.thumbnail?.url}
                isRounded={isThumbnailRounded}
              />
              {isThumbnailVideo && (
                <div
                  data-testid="search-result-card--thumbnail-video"
                  className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/40"
                >
                  <Icon color="inverse">play_circle</Icon>
                </div>
              )}
            </div>

            <div>
              <div className="space-y-1">
                <Text
                  data-testid="search-result-card--content-types"
                  variant="subheading"
                  color="subdued"
                  className="uppercase overflow-wrap-anywhere line-clamp-1"
                >
                  {item?.topic}
                </Text>
                <Text
                  data-testid="search-result-card--title"
                  variant="action"
                  className="overflow-wrap-anywhere line-clamp-3"
                >
                  {item?.title}
                </Text>
                <Text
                  data-testid="search-result-card--description"
                  variant="body-sm"
                  className="overflow-wrap-anywhere line-clamp-3"
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html: xss(item?.description),
                    }}
                  />
                </Text>
              </div>

              {hasTags && (
                <div className="mt-3">
                  {displayedTags.map((tag, index) => {
                    if (Boolean(tag)) {
                      return (
                        <SearchResultTag
                          key={`search-result-${item?.collectionId}-tag-${index}`}
                          data={tag}
                        />
                      )
                    }

                    return null
                  })}
                </div>
              )}
            </div>
          </div>
        </a>
      </NextLink>
    </div>
  )
}

export default SearchResultCard
